import { useLazyQuery, useQuery } from "@apollo/client";
import {
  Badge,
  Button,
  Card,
  Group,
  LoadingOverlay,
  ScrollArea,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { GET_SME_SUPPLIERS } from "apollo/queries";
import B2bTable from "components/reusable/b2bTable";
import SmeSupplierImport from "components/Supplier/SmeSupplierImport";
import { customLoader } from "components/utilities/loader";
import { useEffect, useState } from "react";
import { Download, Upload } from "tabler-icons-react";
import ExportComponent from "utiles/export";
import { formatNumber } from "utiles/url";

const SmeSuppliers = () => {
  const [size, setSize] = useState("10");
  const [searchValue, setSearchValue] = useState("");
  const [confirmedSearch, setConfirmedSearch] = useState("");
  const [activePage, setActivePage] = useState(1);
  const [openedImport, setOpenedImport] = useState(false);

  const handleDownloadTemplate = () => {
    const link = document.createElement("a");
    link.href = "/sme_suppliers.xlsx";
    link.download = "sme_suppliers_template.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const { data, loading, refetch } = useQuery(GET_SME_SUPPLIERS, {
    variables: {
      is_sme: true,
      first: parseInt(size),
      page: activePage,
      search: searchValue,
    },
    onError(error) {
      showNotification({
        color: "red",
        title: "Error",
        message:
          error?.graphQLErrors?.[0]?.message || "Failed to Get Suppliers",
      });
    },
  });

  const [fetchAllSmeSuppliers] = useLazyQuery(GET_SME_SUPPLIERS);

  const handleExportDataFetch = async () => {
    const { data: allData } = await fetchAllSmeSuppliers({
      variables: {
        is_sme: true,
        first: null,
        page: 1,
        search: searchValue,
      },
    });
    return allData?.getSuppliers?.data || [];
  };

  useEffect(() => {
    refetch();
  }, [refetch]);

  const handlePageSizeChange = (newSize) => {
    setSize(newSize);
    setActivePage(1);
  };

  const handleChange = (currentPage) => {
    setActivePage(currentPage);
  };

  const handleManualSearch = (searchTerm) => {
    setSearchValue(searchTerm);
  };

  const clearInput = () => {
    setSearchValue("");
    setConfirmedSearch("");
  };

  const headerData = [
    {
      label: "#",
      key: "index",
      sortable: false,
      searchable: false,
      width: "5%",
      render: (_rowData, rowIndex) => {
        const idx = (activePage - 1) * parseInt(size || "0") + (rowIndex + 1);
        return <span>{idx}</span>;
      },
    },
    {
      label: "Name",
      key: "name_user",
      sortable: true,
      searchable: true,
      render: (rowData) => {
        return <span>{rowData.user.name}</span>;
      },
    },
    {
      label: "Phone",
      key: "Phone",
      sortable: false,
      searchable: true,
      render: (rowData) => {
        return <span>{rowData.user.phone}</span>;
      },
    },
    {
      label: "Email",
      key: "Email",
      sortable: false,
      searchable: true,
      render: (rowData) => {
        return <span>{rowData.user.email}</span>;
      },
    },
    {
      label: "Total Balance",
      key: "total_balance",
      sortable: false,
      searchable: true,
      render: (rowData) => {
        return <span>{formatNumber(rowData.total_balance)}</span>;
      },
    },
    {
      label: "Total Withdrawal",
      key: "total_withdrawal",
      sortable: false,
      searchable: true,
      render: (rowData) => {
        return <span>{formatNumber(rowData.total_withdrawal)}</span>;
      },
    },
    {
      label: "Status",
      key: "account",
      sortable: false,
      exportable: false,
      searchable: false,
      render: (rowData) => {
        return (
          <span>
            {rowData.user.status === "ACTIVE" ? (
              <Badge variant="light" color="green">
                Active
              </Badge>
            ) : (
              <Badge variant="light" color="red">
                Not Active
              </Badge>
            )}
          </span>
        );
      },
    },
  ];

  return (
    <div style={{ width: "98%", margin: "auto" }}>
      <Card shadow="sm" p="lg">
        <ScrollArea>
          {loading ? (
            <LoadingOverlay
              visible={loading}
              color="blue"
              overlayBlur={2}
              loader={customLoader}
            />
          ) : (
            <B2bTable
              activePage={activePage}
              handleChange={handleChange}
              paginatorInfo={data?.getSuppliers?.paginatorInfo}
              header={headerData}
              clearInput={clearInput}
              handelSearch={handleManualSearch}
              filterData={({ onCardClick }) => (
                <Group
                  spacing="xs"
                  style={{ display: "flex", flexWrap: "nowrap" }}
                >
                  <Button
                    style={{
                      backgroundColor: "#FF6A00",
                      color: "#FFFFFF",
                    }}
                    onClick={handleDownloadTemplate}
                    leftIcon={<Download size={16} />}
                  >
                    Download
                  </Button>
                  <Button
                    style={{
                      backgroundColor: "#FF6A00",
                      color: "#FFFFFF",
                    }}
                    onClick={() => setOpenedImport(true)}
                    leftIcon={<Upload size={16} />}
                  >
                    Import
                  </Button>
                  <ExportComponent
                    data={data?.getSuppliers?.data || []}
                    headers={headerData}
                    reportTitle="SME Suppliers Report"
                    onFetch={handleExportDataFetch}
                    fileNamePrefix="sme_suppliers_report"
                  />
                </Group>
              )}
              searchValue={confirmedSearch}
              onSearchChange={setConfirmedSearch}
              loading={loading}
              data={data ? data.getSuppliers.data : []}
              size={size}
              handlePageSizeChange={handlePageSizeChange}
            />
          )}
        </ScrollArea>
      </Card>

      <SmeSupplierImport
        opened={openedImport}
        onClose={() => setOpenedImport(false)}
        onSuccess={() => {
          refetch();
        }}
      />
    </div>
  );
};

export default SmeSuppliers;
